import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { Moment } from 'moment'
import { QueryResult } from '@apollo/client'
import debounce from 'lodash/debounce'

import {
  useGetCalendarDaysForBookingQuery,
  useGetCalendarDaysForListingQuery,
} from 'hooks/useQueries'

import Overlay from '../../display/Overlay'
import Spinner from '../../display/Spinner'

import BookingFormCalendar from './BookingFormCalendarComponent'
import {
  getInitialDataBounds,
  moveDataWindowForward,
  moveDataWindowBackwards,
} from './utils/monthDataBounds'

import { DaysAvailabilities } from './types'

const MONTHS_EITHER_SIDE_OF_ONSCREEN = 2
const MS_FETCHMORE_DEBOUNCE = 500

const PlaceholderInput = styled.div`
  position: relative;
  min-width: 280px;
  min-height: 60px;
  width: 100%;
  overflow: hidden;
  padding: 18px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme?.['-ds-neutral-400']?.value};
  background-color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
`

const formatDateForGql = (date: Moment) => date.format('YYYY-MM-DD')

const availabilitiesToDict = (
  days:
    | GQL_GetCalendarDaysForListingQuery['listing']['calendar']['days']
    | GQL_GetCalendarDaysForBookingQuery['booking']['calendar']['days']
) =>
  days.reduce(
    (daysDict: DaysAvailabilities, day: DaysAvailabilities[string]) => {
      return {
        ...daysDict,
        [day.date]: day,
      }
    },
    {}
  )

type DateRange = { startDate: string; endDate: string }

type BookingDays = QueryResult<
  GQL_GetCalendarDaysForBookingQuery,
  Exact<{
    bookingId: string
    startDate: any
    endDate: any
  }>
>

type ListingDays = QueryResult<
  GQL_GetCalendarDaysForListingQuery,
  Exact<{
    bookingId: string
    startDate: any
    endDate: any
  }>
>

function useCalendarDaysQuery(
  dateRange: DateRange,
  listingId: number,
  bookingId: number
): BookingDays
function useCalendarDaysQuery(
  dateRange: DateRange,
  listingId: number
): ListingDays
function useCalendarDaysQuery(
  dateRange: DateRange,
  listingId: number,
  bookingId?: number
) {
  const resultBooking = useGetCalendarDaysForBookingQuery({
    skip: bookingId == null,
    variables: {
      bookingId: bookingId?.toString(),
      ...dateRange,
    },
  })
  const resultListing = useGetCalendarDaysForListingQuery({
    skip: bookingId != null,
    variables: {
      listingId: listingId.toString(),
      ...dateRange,
    },
  })

  if (bookingId) {
    return resultBooking
  }

  return resultListing
}

interface BookingFormWithDataProps {
  bookingId: number
  dispatch: (...arg: any) => any
  formName: string
  listingId: number
  start_date?: string
  end_date?: string
}

const BookingFormWithData = (props: BookingFormWithDataProps) => {
  const {
    bookingId,
    listingId,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    start_date,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    end_date,
    dispatch,
    formName,
  } = props

  const { initialDataStartDate, initialDataEndDate } = getInitialDataBounds(
    start_date,
    MONTHS_EITHER_SIDE_OF_ONSCREEN
  )

  const [dataStartDate, setDataStartDate] = useState(initialDataStartDate)
  const [dataEndDate, setDataEndDate] = useState(initialDataEndDate)

  useEffect(() => {
    const {
      initialDataStartDate: newInitialStartDate,
      initialDataEndDate: newInitialEndDate,
    } = getInitialDataBounds(start_date, MONTHS_EITHER_SIDE_OF_ONSCREEN)

    setDataStartDate(newInitialStartDate)
    setDataEndDate(newInitialEndDate)
  }, [start_date])

  const dateRange = {
    startDate: formatDateForGql(initialDataStartDate),
    endDate: formatDateForGql(initialDataEndDate),
  }

  const { data, error, loading, fetchMore } = useCalendarDaysQuery(
    dateRange,
    listingId,
    bookingId
  )

  const fetchMoreData = useCallback(
    ({
      nextDataStartDate,
      nextDataEndDate,
    }: {
      nextDataStartDate: Moment
      nextDataEndDate: Moment
    }) =>
      fetchMore({
        variables: {
          [bookingId ? 'bookingId' : 'listingId']: bookingId || listingId,
          startDate: formatDateForGql(nextDataStartDate),
          endDate: formatDateForGql(nextDataEndDate),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return fetchMoreResult
        },
      }),
    [bookingId, listingId]
  )
  const debouncedFetchMoreData = debounce(fetchMoreData, MS_FETCHMORE_DEBOUNCE)

  const onMonthsBack = useCallback(() => {
    const { nextDataStartDate, nextDataEndDate } = moveDataWindowBackwards({
      dataStartDate,
      dataEndDate,
    })

    debouncedFetchMoreData({ nextDataStartDate, nextDataEndDate })
    setDataStartDate(nextDataStartDate)
    setDataEndDate(nextDataEndDate)
  }, [dataStartDate, dataEndDate])

  const onMonthsForward = useCallback(() => {
    const { nextDataStartDate, nextDataEndDate } = moveDataWindowForward({
      dataStartDate,
      dataEndDate,
    })

    debouncedFetchMoreData({ nextDataStartDate, nextDataEndDate })
    setDataStartDate(nextDataStartDate)
    setDataEndDate(nextDataEndDate)
  }, [dataStartDate, dataEndDate])

  if (error) {
    return (
      <div>
        Oops, couldn&apos;t get the booking information for this listing. Please
        let us know!
      </div>
    )
  }

  if (loading || !data) {
    return (
      <PlaceholderInput>
        <Overlay>
          <Spinner small />
        </Overlay>
      </PlaceholderInput>
    )
  }

  let calendar
  if (bookingId) {
    calendar = (data as GQL_GetCalendarDaysForBookingQuery).booking.calendar
  } else {
    calendar = (data as GQL_GetCalendarDaysForListingQuery).listing.calendar
  }

  const { days, maxStartDate } = calendar

  // reduce availability data to a dictionary for more efficient lookup later
  const daysData = availabilitiesToDict(days)

  return (
    <BookingFormCalendar
      dispatch={dispatch}
      formName={formName}
      daysData={daysData}
      listing={calendar.listing}
      maxStartDate={maxStartDate}
      start_date={start_date}
      end_date={end_date}
      onMonthsForward={onMonthsForward}
      onMonthsBack={onMonthsBack}
      snoozed={calendar.listing.snoozed}
    />
  )
}

export default connect()(BookingFormWithData)
